import { useCallback, useRef } from 'react';
import { partsAPI } from '../../../lib/api';
import { useView } from '../../../contexts/ViewContext';
import type { NCRFormData } from './ncrFormTypes';

/**
 * CR-7137: fill the NCR's Part Description from the Parts Master once a part
 * number has been entered.
 *
 * Only a description this lookup put there, or an empty one, is ever replaced:
 * text the user typed, or that a GRN lookup filled from the order line, stays.
 * The part is matched on its exact number (ignoring case) within the view the
 * user is in. When that yields no single description — an unknown part, a part
 * with none, or sites in the view that describe it differently — nothing is
 * filled, and a description this lookup filled for a previous part is cleared.
 *
 * @example
 * const lookupPartDescription = usePartDescriptionLookup(formData, updateField);
 * <Input onBlur={() => lookupPartDescription(formData.part_number)} />
 *
 * @param formData - the Create NCR form state
 * @param updateField - the form's field setter
 * @returns a function to call with the part number once it has been entered
 */
export function usePartDescriptionLookup(
  formData: NCRFormData,
  updateField: (field: keyof NCRFormData, value: string) => void,
): (partNumber: string) => Promise<void> {
  const { getViewQueryParams } = useView();
  const autoFilled = useRef<string | null>(null);
  const description = useRef(formData.description);
  description.current = formData.description;
  // Only the latest lookup may write: an earlier one answering late would
  // describe a part number that is no longer in the field.
  const latest = useRef(0);
  // Enter then Tab asks for the same part twice; the second changes nothing.
  const lastAsked = useRef<string | null>(null);

  return useCallback(async (partNumber: string) => {
    const ownedByLookup = () => {
      const now = description.current.trim();
      return !now || now === autoFilled.current;
    };
    const part = partNumber.trim();
    if (!part || !ownedByLookup()) return;
    const asked = `${part.toLowerCase()}|${description.current}`;
    if (asked === lastAsked.current) return;
    lastAsked.current = asked;
    const request = ++latest.current;
    try {
      const response = await partsAPI.list({
        part_number__iexact: part,
        page_size: 5,
        ...getViewQueryParams(),
      });
      const found = new Set(
        response.data.results.map((p) => (p.description || '').trim()).filter(Boolean),
      );
      // A newer lookup has started, or the user typed while this one was in flight.
      if (request !== latest.current || !ownedByLookup()) return;
      if (found.size === 1) {
        const [text] = Array.from(found);
        autoFilled.current = text;
        updateField('description', text);
      } else if (autoFilled.current) {
        autoFilled.current = null;
        updateField('description', '');
      }
    } catch (err: unknown) {
      lastAsked.current = null; // let the same part be tried again
      console.error('Part description lookup failed:', err);
    }
  }, [getViewQueryParams, updateField]);
}
